import { IMeta, IPagination, ISort } from "@delanobgt/admin-core/util/types";
import { produce } from "immer";
import { Reducer } from "redux";
import {
  ECurrencyRateSortField,
  ICurrencyRate,
  PCurrencyRateFilter,
} from "src/core/currencyRate/entities";
import {
  ActionType,
  createAction,
  createAsyncAction,
  getType,
} from "typesafe-actions";

/*
 * state
 */
export interface ICurrencyRateState {
  currencyRates: ICurrencyRate[];
  loading: boolean;
  error: unknown;
  meta: IMeta;
  filter: PCurrencyRateFilter;
  pagination: IPagination;
  sorts: ISort<ECurrencyRateSortField>[];
  refreshDelay: number;
}

const INITIAL_STATE: ICurrencyRateState = {
  currencyRates: [],
  loading: false,
  error: null,
  meta: {
    limit: 10,
    offset: 0,
    total: 0,
  },
  filter: {},
  pagination: {
    limit: 10,
    offset: 0,
  },
  sorts: [],
  refreshDelay: 5000,
};

/*
 * action types
 */
const FETCH_REQUESTED = "currency-rate/FETCH_REQUESTED";
const FETCH_SUCCEEDED = "currency-rate/FETCH_SUCCEEDED";
const FETCH_FAILED = "currency-rate/FETCH_FAILED";
const SET_FILTER_PAGINATION_SORT = "currency-rate/SET_FILTER_PAGINATION_SORT";
const SET_REFRESH_DELAY = "currency-rate/SET_REFRESH_DELAY";
const POLLING_START_REQUESTED = "currency-rate/POLLING_START_REQUESTED";
const POLLING_STOP_REQUESTED = "currency-rate/POLLING_STOP_REQUESTED";

/*
 * action creators
 */
export const actions = {
  setFilterPaginationSort: createAction(SET_FILTER_PAGINATION_SORT)<{
    filter: PCurrencyRateFilter;
    pagination: IPagination;
    sorts: ISort<ECurrencyRateSortField>[];
  }>(),

  setRefreshDelay: createAction(SET_REFRESH_DELAY)<{
    refreshDelay: number;
  }>(),

  fetchAsync: createAsyncAction(FETCH_REQUESTED, FETCH_SUCCEEDED, FETCH_FAILED)<
    unknown,
    {
      data: ICurrencyRate[];
      meta: IMeta;
    },
    unknown
  >(),

  pollingStartRequested: createAction(POLLING_START_REQUESTED)(),

  pollingStopRequested: createAction(POLLING_STOP_REQUESTED)(),
};
export type TActions = ActionType<typeof actions>;

const reducer: Reducer<ICurrencyRateState, TActions> = (
  state = INITIAL_STATE,
  action
) => {
  return produce<ICurrencyRateState>(state, (draft) => {
    switch (action.type) {
      case getType(actions.setFilterPaginationSort): {
        const { payload } = action;
        draft.filter = payload.filter;
        draft.pagination = payload.pagination;
        draft.sorts = payload.sorts;
        break;
      }
      case getType(actions.setRefreshDelay): {
        const { payload } = action;
        draft.refreshDelay = payload.refreshDelay;
        break;
      }
      case getType(actions.fetchAsync.request): {
        draft.loading = true;
        draft.error = null;
        break;
      }
      case getType(actions.fetchAsync.success): {
        const {
          payload: { data, meta },
        } = action;
        draft.currencyRates = data;
        draft.loading = false;
        draft.error = null;
        draft.meta = meta;
        break;
      }
      case getType(actions.fetchAsync.failure): {
        const { payload } = action;
        draft.loading = false;
        draft.error = payload;
        break;
      }
    }
  });
};

export default reducer;
